import React from 'react';

export default function TeamMemberPagination({
  currentPage,
  totalPages,
  totalResults,
  itemsPerPage,
  onPageChange
}) {
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const handlePageChange = (pageNumber) => {
    onPageChange(pageNumber);
  };

  if (totalResults === 0) {
    return null;
  }

  return (
    <div className="mt-4 flex items-center justify-between border-t border-gray-200 px-4 py-3 sm:px-6">
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{" "}
            <span className="font-medium">
              {Math.min(indexOfLastItem, totalResults)}
            </span>{" "}
            of <span className="font-medium">{totalResults}</span> results
          </p>
        </div>
        <div>
          <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
            <button
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 
              ${currentPage === 1 ? 'cursor-not-allowed' : 'hover:bg-gray-50'}`}
            >
              <span className="sr-only">Previous</span>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
            
            {/* Page number buttons */}
            {Array.from({ length: Math.min(5, totalPages) }).map((_, i) => {
              // For simplicity, show max 5 pages and center the current page
              const maxPages = Math.min(5, totalPages);
              let pageNum;
              
              if (totalPages <= 5) {
                pageNum = i + 1; // Just show pages 1-5 if there are 5 or fewer
              } else {
                // For more than 5 pages, create a sliding window
                let startPage = Math.max(1, currentPage - Math.floor(maxPages / 2));
                if (startPage + maxPages > totalPages) {
                  startPage = totalPages - maxPages + 1;
                }
                pageNum = startPage + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold 
                    ${currentPage === pageNum
                      ? 'z-10 bg-sky-600 text-white focus:z-20 focus-visible:outline-sky-600'
                      : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20'
                    }`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className={`relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 
              ${currentPage === totalPages ? 'cursor-not-allowed' : 'hover:bg-gray-50'}`}
            >
              <span className="sr-only">Next</span>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.414l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
              </svg>
            </button>
          </nav>
        </div>
      </div>
      
      {/* Mobile pagination controls */}
      <div className="flex flex-1 justify-between sm:hidden">
        <button
          onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className={`relative inline-flex items-center rounded-md px-4 py-2 text-sm font-medium text-gray-700 
          ${currentPage === 1 ? 'cursor-not-allowed' : 'hover:bg-gray-50'}`}
        >
          Previous
        </button>
        <div className="text-sm text-gray-700">
          Page {currentPage} of {totalPages}
        </div>
        <button
          onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className={`relative ml-3 inline-flex items-center rounded-md px-4 py-2 text-sm font-medium text-gray-700 
          ${currentPage === totalPages ? 'cursor-not-allowed' : 'hover:bg-gray-50'}`}
        >
          Next
        </button>
      </div>
    </div>
  );
} 